import { useState, useEffect, useMemo } from 'react'
import { CheckCircle, Circle, AlertCircle, TrendingUp } from 'lucide-react'
import OKRDetailModal from '../components/OKRDetailModal'
import OKRFilters from '../components/OKRFilters'

interface KeyResult {
  id: number
  objective_title: string
  objective_description: string
  target_value: number
  current_value: number
  unit: string
  status: string
  progress_percentage: number
  owner_name: string
  owner_email: string
  start_date: string
  end_date: string
  priority: string
}

interface Objective {
  id: number
  title: string
  priority: string
  keyResults: KeyResult[]
}

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
  'MEDIUM-HIGH': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  MEDIUM: 'bg-blue-100 text-blue-800 border-blue-300'
}

export default function OKRsPage() {
  const [objectives, setObjectives] = useState<Objective[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedQuarter, setSelectedQuarter] = useState('Q4')
  const [selectedYear, setSelectedYear] = useState(2025)
  const [selectedOKR, setSelectedOKR] = useState<KeyResult | null>(null)
  const [filters, setFilters] = useState<{
    priority: string | null
    status: string | null
    owner: string | null
  }>({
    priority: null,
    status: null,
    owner: null,
  })

  useEffect(() => {
    fetchOKRs()
  }, [selectedQuarter, selectedYear])

  // Extract unique owners from objectives
  const owners = useMemo(() => {
    const ownerMap = new Map<string, { id: string; name: string }>()
    objectives.forEach(obj => {
      obj.keyResults.forEach(kr => {
        if (!ownerMap.has(kr.owner_email)) {
          ownerMap.set(kr.owner_email, {
            id: kr.owner_email,
            name: kr.owner_name,
          })
        }
      })
    })
    return Array.from(ownerMap.values())
  }, [objectives])

  // Filter objectives based on active filters
  const filteredObjectives = useMemo(() => {
    return objectives.map(obj => {
      // Filter priority at objective level
      if (filters.priority && obj.priority !== filters.priority) {
        return { ...obj, keyResults: [] }
      }

      // Filter key results
      const filteredKRs = obj.keyResults.filter(kr => {
        if (filters.status && kr.status !== filters.status) return false
        if (filters.owner && kr.owner_email !== filters.owner) return false
        return true
      })

      return { ...obj, keyResults: filteredKRs }
    }).filter(obj => obj.keyResults.length > 0) // Remove objectives with no matching key results
  }, [objectives, filters])

  const fetchOKRs = async () => {
    try {
      const response = await fetch(
        `/api/okrs?quarter=${selectedQuarter}&year=${selectedYear}`
      )
      const result = await response.json()
      if (result.success) {
        setObjectives(result.data)
      }
    } catch (error) {
      console.error('Error fetching OKRs:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveOKR = async (updatedOkr: any) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/okrs/${updatedOkr.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedOkr),
      })

      if (!response.ok) {
        throw new Error('Failed to update OKR')
      }

      // Refetch OKRs to get updated data
      await fetchOKRs()
      setSelectedOKR(null)
    } catch (error) {
      console.error('Error saving OKR:', error)
      throw error
    }
  }

  const handleClearFilters = () => {
    setFilters({
      priority: null,
      status: null,
      owner: null,
    })
  }

  const getStatusIcon = (progress: number) => {
    if (progress >= 100) return <CheckCircle className="w-5 h-5 text-green-600" />
    if (progress >= 70) return <TrendingUp className="w-5 h-5 text-blue-600" />
    if (progress >= 30) return <AlertCircle className="w-5 h-5 text-yellow-600" />
    return <Circle className="w-5 h-5 text-gray-400" />
  }

  const calculateObjectiveProgress = (keyResults: KeyResult[]) => {
    if (!keyResults.length) return 0
    const total = keyResults.reduce((sum, kr) => sum + kr.progress_percentage, 0)
    return Math.round(total / keyResults.length)
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-vampire-black">
            Q4 2025 OKRs
          </h1>
          <p className="text-graphite-grey mt-2">
            Strategic objectives and key results for Advisory Division
          </p>
        </div>

        <div className="flex gap-2">
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <OKRFilters
        filters={filters}
        owners={owners}
        onFilterChange={setFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Overall Progress Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {filteredObjectives.map((obj) => {
          const progress = calculateObjectiveProgress(obj.keyResults)
          return (
            <div
              key={obj.id}
              className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm font-semibold text-graphite-grey">
                  Objective {obj.id}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full border ${priorityColors[obj.priority]}`}>
                  {obj.priority}
                </span>
              </div>
              <div className="mb-2">
                <div className="text-2xl font-bold text-royal-blue">{progress}%</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-royal-blue h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Objectives and Key Results */}
      <div className="space-y-6">
        {filteredObjectives.map((objective) => (
          <div
            key={objective.id}
            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
          >
            {/* Objective Header */}
            <div className={`p-6 border-l-4 ${
              objective.priority === 'CRITICAL' ? 'border-red-500' :
              objective.priority === 'HIGH' ? 'border-orange-500' :
              objective.priority === 'MEDIUM-HIGH' ? 'border-yellow-500' :
              'border-blue-500'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-vampire-black">
                      Objective {objective.id}: {objective.title}
                    </h2>
                    <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${priorityColors[objective.priority]}`}>
                      {objective.priority}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="text-sm text-graphite-grey">
                      Overall Progress: {calculateObjectiveProgress(objective.keyResults)}%
                    </span>
                    <div className="w-full bg-gray-200 rounded-full h-3 mt-1">
                      <div
                        className="bg-royal-blue h-3 rounded-full transition-all"
                        style={{ width: `${calculateObjectiveProgress(objective.keyResults)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Results */}
            <div className="divide-y divide-gray-100">
              {objective.keyResults.map((kr) => (
                <div
                  key={kr.id}
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedOKR(kr)}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(kr.progress_percentage)}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-vampire-black">
                          {kr.objective_title}
                        </h3>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-royal-blue">
                            {kr.current_value} / {kr.target_value} {kr.unit}
                          </div>
                          <div className="text-xs text-graphite-grey">
                            {kr.progress_percentage}% complete
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-graphite-grey mb-3">
                        {kr.objective_description}
                      </p>

                      <div className="flex items-center justify-between text-xs text-graphite-grey">
                        <span>Owner: {kr.owner_name}</span>
                        <span>
                          {new Date(kr.start_date).toLocaleDateString()} -
                          {new Date(kr.end_date).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            kr.progress_percentage >= 100 ? 'bg-green-500' :
                            kr.progress_percentage >= 70 ? 'bg-blue-500' :
                            kr.progress_percentage >= 30 ? 'bg-yellow-500' :
                            'bg-gray-400'
                          }`}
                          style={{ width: `${Math.min(kr.progress_percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* OKR Detail Modal */}
      {selectedOKR && (
        <OKRDetailModal
          okr={selectedOKR}
          isOpen={!!selectedOKR}
          onClose={() => setSelectedOKR(null)}
          onSave={handleSaveOKR}
        />
      )}
    </div>
  )
}
